import type * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDictationLanguages } from '@/hooks/useDictationLanguages';
import { backendClient } from '@/lib/api-client';
import { createLogger } from '@/lib/logger';

const logger = createLogger('use-dictation');

// The Speech SDK is ~7 MB, so it's kept out of the main bundle and imported on demand.
// The promise is module-level so every hook instance shares a single fetch/parse, and
// callers can prefetch it (e.g. when the Case Partner screen mounts) so there's no
// first-click latency when dictation actually starts.
type SpeechSdkModule = typeof import('microsoft-cognitiveservices-speech-sdk');
let speechSdkPromise: Promise<SpeechSdkModule> | null = null;
function loadSpeechSdk(): Promise<SpeechSdkModule> {
  if (!speechSdkPromise) {
    // Clear the cache on failure so a transient load error (e.g. the mount prefetch
    // hitting a network blip) doesn't leave a permanently-rejected promise that makes
    // every later start() fail until a full page reload. Success stays cached.
    speechSdkPromise = import('microsoft-cognitiveservices-speech-sdk').catch((err) => {
      speechSdkPromise = null;
      throw err;
    });
  }
  return speechSdkPromise;
}

// Safety buffer on top of a full session's worth of token life, so a reused token can
// never expire right at the session's hard cap.
const TOKEN_REFRESH_SKEW_MS = 60 * 1000;

// True when mic permission is already granted, so we can skip the extra getUserMedia
// probe (a second device open) and let the SDK acquire the mic directly.
async function micPermissionGranted(): Promise<boolean> {
  try {
    const status = await navigator.permissions.query({
      name: 'microphone' as PermissionName,
    });
    return status.state === 'granted';
  } catch {
    // Permissions API unavailable for mic (e.g. Firefox): fall back to probing.
    return false;
  }
}

// Hard client-side cap on a single dictation session. Keeps a forgotten open mic
// from streaming (and billing, once metering returns) indefinitely; the user just
// clicks the mic again to continue.
const MAX_SESSION_MS = 5 * 60 * 1000;

// Auto-stop after this much continuous silence (no interim/final speech events).
// Catches the common "walked away / forgot to stop" case long before the hard cap,
// while still tolerating natural pauses to think mid-sentence. Exported so UI copy
// (e.g. the idle-stop toast) can derive its wording from the single source of truth.
export const IDLE_TIMEOUT_MS = 10 * 1000;
// Longer grace before the *first* recognition result arrives.
const INITIAL_IDLE_TIMEOUT_MS = 10 * 1000;

// Only reuse a cached token if it has enough life left to outlast an entire session
// (plus a buffer). The recognizer's authorization token isn't refreshed after connect,
// so a token that expires mid-session would drop it; requiring more than a full
// session's worth of remaining life guarantees a reused token covers the whole session.
const TOKEN_MIN_REMAINING_MS = MAX_SESSION_MS + TOKEN_REFRESH_SKEW_MS;

interface DictationTokenResponse {
  /** Short-lived Speech authorization token (STS issueToken JWT), scoped to the resource. */
  token: string;
  /** Azure region of the Speech resource, e.g. "uksouth". */
  region: string;
  /** Candidate recognition locales (first is default); >1 enables auto-detection. */
  languages: string[];
  /** Unix epoch seconds at which the token expires. */
  expires_at: number;
}

export interface UseDictationParams {
  /** Matter GUID the dictation is scoped to (required to connect). */
  matterGuid?: string;
  /** Active chat GUID. When it (or the matter) changes, any live session is stopped so
   * audio can't bleed from one conversation's composer into another. */
  chatGuid?: string | null;
  /** Current chat input value (used as the base to append transcript onto). */
  value: string;
  /** Setter for the chat input value. */
  onChange: (value: string) => void;
  /** Called when a live session auto-stops after IDLE_TIMEOUT_MS of silence (e.g. to
   * surface a toast). Not called for manual stops or the max-session hard cap. */
  onIdleStop?: () => void;
}

export interface UseDictationResult {
  isSupported: boolean;
  isRecording: boolean;
  isConnecting: boolean;
  error: string | null;
  clearError: () => void;
  start: () => Promise<void>;
  /** Stop dictation. Pass { discard: true } to drop any trailing final (send/switch). */
  stop: (options?: { discard?: boolean }) => void;
  toggle: () => void;
  /** Prefetch SDK + token (e.g. on mic-button hover) to cut click latency. */
  prewarm: () => void;
}

function browserSupportsDictation(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    (typeof AudioContext !== 'undefined' ||
      typeof (window as unknown as { webkitAudioContext?: unknown }).webkitAudioContext !==
        'undefined')
  );
}

export function composeTranscript(base: string, committed: string, interim: string): string {
  const spoken = [committed, interim].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
  if (!spoken) return base;
  if (!base) return spoken;
  const needsSpace = !/\s$/.test(base);
  return `${base}${needsSpace ? ' ' : ''}${spoken}`;
}

function httpStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } }).response?.status;
}

/**
 * Click-to-speak dictation for the Case Partner chat input. Fetches a short-lived
 * Azure Speech authorization token from the backend, then streams the mic straight
 * to Azure with the Speech JS SDK (no backend audio relay) and writes interim +
 * final transcripts into the chat input live. Fill-and-review: text lands in the box
 * for the user to edit and send; nothing auto-sends.
 */
export function useDictation({
  matterGuid,
  chatGuid,
  value,
  onChange,
  onIdleStop,
}: UseDictationParams): UseDictationResult {
  const [isRecording, setIsRecording] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSupported = browserSupportsDictation();

  // Latest input value, so start() captures the correct base without re-binding.
  const valueRef = useRef(value);
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const onIdleStopRef = useRef(onIdleStop);
  useEffect(() => {
    onIdleStopRef.current = onIdleStop;
  }, [onIdleStop]);

  const recognizerRef = useRef<SpeechSDK.SpeechRecognizer | null>(null);
  const sdkRef = useRef<SpeechSdkModule | null>(null);
  // Stable text preceding the live hypothesis: the user's starting text plus any
  // finalised (recognized) speech. The live interim is never folded in here.
  const baseTextRef = useRef('');
  // The interim hypothesis currently shown at the tail of the composer. Azure resends
  // the whole in-progress utterance each event, so we treat this as a replaceable tail
  // rather than appending cumulatively (which would duplicate words).
  const lastInterimRef = useRef('');
  // The exact string this hook last pushed into the composer. Lets us tell our own
  // echo apart from the user editing the box mid-session, so manual edits aren't
  // clobbered by the next speech event.
  const lastWrittenRef = useRef('');
  const stoppingRef = useRef(false);
  // Set when a stop should abandon in-flight speech entirely (send, context switch,
  // unmount) so no trailing final repopulates a just-cleared/foreign composer. A plain
  // manual/idle/cap stop leaves this false so the last final still lands.
  const discardRef = useRef(false);
  const maxSessionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // False until the first recognition result with text arrives. While false, the idle
  // timer uses the longer INITIAL_IDLE_TIMEOUT_MS (first-word grace); after, IDLE_TIMEOUT_MS.
  const heardSpeechRef = useRef(false);
  // Monotonic id for the current start attempt. Bumped by start() (new attempt) and by
  // stop()/unmount (cancellation), so an async start() can detect it was superseded
  // after any await and bail before opening the mic.
  const sessionGenRef = useRef(0);
  const mountedRef = useRef(true);

  // Last brokered Speech token, reused across a prewarm + click (and quick repeat
  // sessions) while it's still valid, so the click path usually skips the backend ->
  // Azure STS round-trip entirely.
  const tokenCacheRef = useRef<DictationTokenResponse | null>(null);
  const tokenInflightRef = useRef<Promise<DictationTokenResponse> | null>(null);

  // Candidate locales come from the user's *current* dictation settings (shared SWR
  // cache), not from the cached token. The brokered token also carries a languages
  // snapshot, but the token is reused for minutes, so relying on it would keep starting
  // sessions with a stale set after the user changes languages while a Case Partner
  // instance stays mounted. Reading the shared cache (which a settings save updates via
  // mutate) means language edits take effect on the next start, no matter the token TTL.
  const { data: dictationLanguagesData } = useDictationLanguages();
  const languagesRef = useRef<string[] | null>(null);
  useEffect(() => {
    // Mirror the backend candidate list: region default first, then the user's
    // (already-sanitised) additional languages. Guard the shape: a partial/malformed
    // response (e.g. missing `selected`) must not throw and take down the composer —
    // fall back to the token's snapshot by leaving languagesRef null.
    const base = dictationLanguagesData?.default;
    const selected = dictationLanguagesData?.selected;
    languagesRef.current = base
      ? [base, ...(Array.isArray(selected) ? selected : [])]
      : null;
  }, [dictationLanguagesData]);

  // Current session's metadata (wall-clock start + candidate locales), set once a
  // session actually begins streaming and cleared when it ends. A single slot is enough
  // since only one session is active at a time; a rare quick stop-then-start overlap may
  // drop the first segment's report, which is acceptable for usage tracking.
  const sessionMetaRef = useRef<{ startedAt: number; languages: string[] } | null>(null);

  // Fire-and-forget: report a completed session's length for usage tracking. Best-effort
  // telemetry — a failure here must never disrupt the mic UX, so errors are swallowed.
  const reportUsage = useCallback((durationMs: number, languages: string[]) => {
    if (durationMs <= 0) return;
    void backendClient
      .post('/case-partner/dictation-usage', { duration_ms: Math.round(durationMs), languages })
      .catch(() => {
        /* best-effort; never surface telemetry failures to the user */
      });
  }, []);

  const fetchToken = useCallback(async (): Promise<DictationTokenResponse> => {
    const cached = tokenCacheRef.current;
    if (cached && cached.expires_at * 1000 - Date.now() > TOKEN_MIN_REMAINING_MS) {
      return cached;
    }
    if (tokenInflightRef.current) return tokenInflightRef.current;
    const request = backendClient
      .get<DictationTokenResponse>('/case-partner/dictation-token')
      .then((response) => {
        tokenCacheRef.current = response.data;
        return response.data;
      })
      .finally(() => {
        tokenInflightRef.current = null;
      });
    tokenInflightRef.current = request;
    return request;
  }, []);

  const teardownRecognizer = useCallback(() => {
    if (maxSessionTimerRef.current) {
      clearTimeout(maxSessionTimerRef.current);
      maxSessionTimerRef.current = null;
    }
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    // Report the just-ended session's length (best-effort telemetry) before disposing.
    // Doing it here — rather than in finishRecording — means sessions torn down on
    // unmount are still counted
    // trailing stop/cancel callback also reaches teardown.
    const meta = sessionMetaRef.current;
    if (meta) {
      sessionMetaRef.current = null;
      reportUsage(Date.now() - meta.startedAt, meta.languages);
    }
    const recognizer = recognizerRef.current;
    recognizerRef.current = null;
    if (recognizer) {
      try {
        recognizer.close();
      } catch {
        /* ignore */
      }
    }
  }, [reportUsage]);

  const finishRecording = useCallback(
    (recognizer?: SpeechSDK.SpeechRecognizer) => {
      // A stop/cancel callback can fire after the user has already started a new
      // session (recognizerRef now holds a different recognizer). In that case just
      // dispose the stale one; never touch the live session's state or ref.
      if (recognizer && recognizerRef.current !== recognizer) {
        try {
          recognizer.close();
        } catch {
          /* ignore */
        }
        return;
      }
      setIsRecording(false);
      setIsConnecting(false);
      stoppingRef.current = false;
      discardRef.current = false;
      teardownRecognizer();
    },
    [teardownRecognizer],
  );

  // Push text into the composer while keeping our own bookkeeping in sync, so the next
  // speech event can distinguish this echo from a manual edit.
  const writeValue = useCallback((next: string) => {
    lastWrittenRef.current = next;
    // Track our own write immediately (don't wait for the value prop to round-trip),
    // so rapid back-to-back speech events don't misread the lag as a user edit.
    valueRef.current = next;
    onChangeRef.current(next);
  }, []);

  // Reconcile our stable base with the current composer before applying a speech event.
  // If the box still matches our last write, nothing changed. Otherwise the user edited
  // it (typed, picked a prompt, etc.): keep their edit to the *stable* text by stripping
  // the volatile interim tail we appended, so the next hypothesis replaces that tail
  // instead of duplicating it (e.g. avoids "hello" -> edit -> "hello hello world"). If
  // the edit landed inside the volatile tail itself, we can't split it cleanly, so we
  // keep our known base — that in-progress hypothesis is about to be replaced anyway.
  const rebaseIfEdited = useCallback(() => {
    const current = valueRef.current;
    if (current === lastWrittenRef.current) return;
    const tail = lastInterimRef.current;
    if (tail && current.endsWith(tail)) {
      baseTextRef.current = current.slice(0, current.length - tail.length).replace(/\s+$/, '');
    } else if (!tail) {
      baseTextRef.current = current;
    }
  }, []);

  const applyInterim = useCallback(
    (interim: string) => {
      // Once a stop is in flight (user stopped, sent, or a timer fired) we must not
      // write interim hypotheses back — the input may have just been cleared/submitted.
      if (stoppingRef.current) return;
      rebaseIfEdited();
      lastInterimRef.current = interim;
      writeValue(composeTranscript(baseTextRef.current, '', interim));
    },
    [rebaseIfEdited, writeValue],
  );

  const commitFinal = useCallback(
    (finalText: string) => {
      // Unlike interims, a final may legitimately arrive during a graceful stop (the
      // recognizer flushes the last utterance). Only drop it when we're abandoning the
      // session (send/context switch/unmount), so manual/idle/cap stops keep the tail.
      if (discardRef.current) return;
      rebaseIfEdited();
      // Fold the final into the stable base and clear the volatile interim tail.
      baseTextRef.current = composeTranscript(baseTextRef.current, '', finalText.trim());
      lastInterimRef.current = '';
      writeValue(baseTextRef.current);
    },
    [rebaseIfEdited, writeValue],
  );

  // discard=true abandons in-flight speech (used on send / context switch) so a trailing
  // final can't repopulate a cleared or now-foreign composer. A plain stop() keeps it.
  const stop = useCallback((options?: { discard?: boolean }) => {
    // Invalidate any in-flight start() so a pending await can't reopen the mic.
    sessionGenRef.current += 1;
    if (options?.discard) discardRef.current = true;
    if (stoppingRef.current) return;
    stoppingRef.current = true;
    // Release the mic immediately for responsiveness; teardown completes once the
    // recognizer confirms it has stopped (last final may still arrive before then).
    setIsRecording(false);
    const recognizer = recognizerRef.current;
    if (recognizer) {
      // Scope teardown to this recognizer: if the user restarts before the async stop
      // completes, the callback must not tear down the new session's recognizer.
      recognizer.stopContinuousRecognitionAsync(
        () => finishRecording(recognizer),
        () => finishRecording(recognizer),
      );
    } else {
      finishRecording();
    }
  }, [finishRecording]);

  // Restart the silence countdown; called whenever speech is detected. If it ever
  // fires, the user has gone quiet for IDLE_TIMEOUT_MS, so end the session and let the
  // caller know it was an idle auto-stop (distinct from a manual stop or hard cap).
  const bumpIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    // Give the first word a longer grace; tighten to the mid-speech idle timeout once
    // we've actually heard something.
    const timeout = heardSpeechRef.current ? IDLE_TIMEOUT_MS : INITIAL_IDLE_TIMEOUT_MS;
    idleTimerRef.current = setTimeout(() => {
      stop();
      onIdleStopRef.current?.();
    }, timeout);
  }, [stop]);

  const handleCanceled = useCallback(
    (details: string, isError: boolean) => {
      // A clean end-of-stop is not an error.
      if (!isError) {
        return;
      }
      const lower = details.toLowerCase();
      if (/too many requests|429|quota/.test(lower)) {
        setError('Dictation is busy right now — please type your message.');
      } else if (/authoriz|token|forbidden|401|403/.test(lower)) {
        // The cached token is no longer trusted; force a fresh broker next attempt.
        tokenCacheRef.current = null;
        setError('Voice dictation session expired — please try again.');
      } else {
        setError('Voice dictation failed — please type your message.');
      }
      logger.error('Dictation canceled', new Error(details || 'unknown'));
    },
    [],
  );

  const start = useCallback(async () => {
    if (isRecording || isConnecting) return;
    if (!isSupported) {
      setError('Voice dictation is not supported in this browser.');
      return;
    }
    if (!matterGuid) {
      setError('Open a matter to use voice dictation.');
      return;
    }

    setError(null);
    setIsConnecting(true);
    // Claim this start attempt. If stop()/unmount/another start bumps the counter while
    // we're awaiting below, we abort instead of opening the mic for a dead session.
    sessionGenRef.current += 1;
    const gen = sessionGenRef.current;
    baseTextRef.current = valueRef.current;
    lastInterimRef.current = '';
    // Seed our echo tracker with the starting text so the first speech event doesn't
    // mistake the untouched composer for a manual edit.
    lastWrittenRef.current = valueRef.current;
    stoppingRef.current = false;
    discardRef.current = false;
    heardSpeechRef.current = false;

    const superseded = () => sessionGenRef.current !== gen || !mountedRef.current;
    const abort = () => {
      // Only clear the connecting flag if this attempt is still the current one. A
      // fast double-click can start a second attempt (bumping the generation) while
      // this one is mid-await; when this stale attempt bails it must not flip the UI
      // to idle out from under the newer attempt that's still connecting.
      if (mountedRef.current && sessionGenRef.current === gen) setIsConnecting(false);
    };

    // Usually already prefetched on mount; await guards a fast first click.
    let sdk: SpeechSdkModule;
    try {
      sdk = sdkRef.current ?? (await loadSpeechSdk());
      sdkRef.current = sdk;
    } catch (err) {
      logger.error('Failed to load Speech SDK', err as Error);
      setError('Could not start dictation — please try again.');
      abort();
      return;
    }
    if (superseded()) return abort();

    // Prompt for the mic up front so denial gives a clear message; the SDK reuses the
    // now-granted permission when it opens its own stream (no second prompt). Skip the
    // probe once permission is already granted — the SDK opens the mic itself, so a
    // second device open here only adds latency (revocation surfaces via canceled).
    if (!(await micPermissionGranted())) {
      try {
        const probe = await navigator.mediaDevices.getUserMedia({ audio: true });
        probe.getTracks().forEach((track) => {
          track.stop();
        });
      } catch (err) {
        logger.info('Microphone permission denied or unavailable', { error: String(err) });
        setError('Microphone access was blocked. Allow microphone access and try again.');
        abort();
        return;
      }
      if (superseded()) return abort();
    }

    let creds: DictationTokenResponse;
    try {
      // Usually already brokered by prewarm() on hover/focus, so this resolves instantly.
      creds = await fetchToken();
    } catch (err) {
      const status = httpStatus(err);
      logger.error('Failed to fetch dictation token', err as Error);
      if (status === 403) {
        setError('Voice dictation is not enabled for your account.');
      } else if (status === 503) {
        setError('Voice dictation is not available right now.');
      } else {
        setError('Could not start dictation — please type your message.');
      }
      abort();
      return;
    }
    if (superseded()) return abort();

    // Prefer the live settings-sourced candidates so a language change mid-mount takes
    // effect immediately; fall back to the token's snapshot until settings have loaded.
    const languages = languagesRef.current?.length
      ? languagesRef.current
      : creds.languages?.length
        ? creds.languages
        : ['en-GB'];
    const multiLanguage = languages.length > 1;
    let recognizer: SpeechSDK.SpeechRecognizer;
    try {
      // The backend brokers a short-lived, resource-scoped token from the Speech
      // /issueToken STS endpoint. Language identification is built on the universal v2
      // endpoint, so multi-language sessions must be built with
      // fromEndpoint(.../speech/universal/v2) (authorised via the brokered token) rather
      // than fromAuthorizationToken, which pins the connection to a single-locale
      // endpoint. Single-language sessions use fromAuthorizationToken as before.
      const speechConfig = multiLanguage
        ? sdk.SpeechConfig.fromEndpoint(
            new URL(`wss://${creds.region}.stt.speech.microsoft.com/speech/universal/v2`),
          )
        : sdk.SpeechConfig.fromAuthorizationToken(creds.token, creds.region);
      if (multiLanguage) {
        // fromEndpoint takes no key, so authorise the connection with the brokered token.
        speechConfig.authorizationToken = creds.token;
      }
      const audioConfig = sdk.AudioConfig.fromDefaultMicrophoneInput();
      if (multiLanguage) {
        // Detect the spoken language among the candidates once, at the start of the
        // session (AtStart LID), then transcribe in that locale. Continuous LID — which
        // re-identifies the language at every segment — adds substantial latency (per
        // Azure's docs, ~0.5-3s, growing with candidate count) and only streams results
        // at segmentation boundaries, which made dictation feel laggy. Dictation is a
        // single message in one language, so AtStart gives fast, word-by-word interims
        // while still auto-detecting the language. Don't pin speechRecognitionLanguage —
        // a fixed reco locale suppresses detection of the other candidates.
        speechConfig.setProperty(
          sdk.PropertyId.SpeechServiceConnection_LanguageIdMode,
          'AtStart',
        );
        const autoDetect = sdk.AutoDetectSourceLanguageConfig.fromLanguages(languages);
        recognizer = sdk.SpeechRecognizer.FromConfig(speechConfig, autoDetect, audioConfig);
      } else {
        speechConfig.speechRecognitionLanguage = languages[0];
        recognizer = new sdk.SpeechRecognizer(speechConfig, audioConfig);
      }
    } catch (err) {
      logger.error('Failed to initialise dictation recognizer', err as Error);
      setError('Could not start dictation — please type your message.');
      abort();
      return;
    }
    // Cancelled while building the recognizer: dispose it so its mic never opens.
    if (superseded()) {
      try {
        recognizer.close();
      } catch {
        /* ignore */
      }
      return abort();
    }
    recognizerRef.current = recognizer;

    // A recognizer that's no longer the live one (user restarted, or a stale instance
    // is still flushing) must never touch the current session's transcript or state.
    // recognizerRef stays pointed at this instance through a graceful stop's final
    // flush, so the last utterance is still captured; it only differs once a new
    // session has taken over.
    const isCurrent = () => recognizerRef.current === recognizer;

    recognizer.recognizing = (_sender, event) => {
      if (!isCurrent()) return;
      if (event.result.text) {
        heardSpeechRef.current = true;
        bumpIdleTimer();
        applyInterim(event.result.text);
      }
    };
    recognizer.recognized = (_sender, event) => {
      if (!isCurrent()) return;
      if (event.result.reason === sdk.ResultReason.RecognizedSpeech && event.result.text) {
        heardSpeechRef.current = true;
        bumpIdleTimer();
        commitFinal(event.result.text);
      }
    };
    recognizer.canceled = (_sender, event) => {
      if (!isCurrent()) {
        // Stale instance — just dispose it, don't surface an error for a dead session.
        finishRecording(recognizer);
        return;
      }
      handleCanceled(event.errorDetails ?? '', event.reason === sdk.CancellationReason.Error);
      finishRecording(recognizer);
    };

    // Open the websocket up front so the TLS/auth/upgrade handshake overlaps with mic
    // acquisition instead of adding to the time-to-first-word. Best-effort: any failure
    // surfaces through the normal start/canceled paths, so ignore errors here.
    try {
      sdk.Connection.fromRecognizer(recognizer).openConnection();
    } catch {
      /* ignore — startContinuousRecognitionAsync will still establish the connection */
    }

    recognizer.startContinuousRecognitionAsync(
      () => {
        // The connection can fail (canceled event) — or the session can be stopped —
        // before this success callback runs. Bail so we don't flip back to a
        // "recording" state (button stuck flashing) for an already torn-down session.
        if (sessionGenRef.current !== gen || recognizerRef.current !== recognizer) return;
        setIsConnecting(false);
        setIsRecording(true);
        // Mark the true session start (mic now streaming) for usage tracking.
        sessionMetaRef.current = { startedAt: Date.now(), languages: [...languages] };
        maxSessionTimerRef.current = setTimeout(() => stop(), MAX_SESSION_MS);
        // Arm the idle countdown up front so a session that never hears any speech
        // (e.g. mic opened by accident) still auto-stops.
        bumpIdleTimer();
      },
      (err) => {
        logger.error('Failed to start dictation recognition', new Error(String(err)));
        setError('Could not start dictation — please type your message.');
        finishRecording(recognizer);
      },
    );
  }, [
    applyInterim,
    bumpIdleTimer,
    commitFinal,
    fetchToken,
    finishRecording,
    handleCanceled,
    isConnecting,
    isRecording,
    isSupported,
    matterGuid,
    stop,
  ]);

  // Warm up the click path ahead of time: called on mic-button hover/focus so the
  // (network-bound) token broker call is already done when the user actually clicks.
  // No mic is opened here — that only happens on start(), on an explicit click.
  const prewarm = useCallback(() => {
    if (!isSupported || !matterGuid || isRecording || isConnecting) return;
    void loadSpeechSdk()
      .then((sdk) => {
        sdkRef.current = sdk;
      })
      .catch(() => {
        /* best-effort; start() surfaces real load errors */
      });
    void fetchToken().catch(() => {
      /* best-effort; start() surfaces real token errors */
    });
  }, [isSupported, matterGuid, isRecording, isConnecting, fetchToken]);

  const toggle = useCallback(() => {
    if (isRecording || isConnecting) {
      stop();
    } else {
      void start();
    }
  }, [isConnecting, isRecording, start, stop]);

  const clearError = useCallback(() => setError(null), []);

  // Prefetch the Speech SDK when the hook mounts (i.e. the Case Partner screen is open)
  // so the first dictation click has no download/parse latency, while still keeping the
  // ~7 MB dependency out of the main bundle.
  useEffect(() => {
    if (!isSupported) return;
    loadSpeechSdk()
      .then((sdk) => {
        sdkRef.current = sdk;
      })
      .catch((err) => {
        logger.warn('Failed to prefetch Speech SDK', { error: String(err) });
      });
  }, [isSupported]);

  // End any live session when the conversation context changes. Case Partner stays
  // mounted across matter/chat switches, so without this, audio started in one
  // conversation would keep writing into another's composer. Skips the initial mount.
  const stopRef = useRef(stop);
  useEffect(() => {
    stopRef.current = stop;
  }, [stop]);
  const contextKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const key = `${matterGuid ?? ''}:${chatGuid ?? ''}`;
    if (contextKeyRef.current === null) {
      contextKeyRef.current = key; // first mount — nothing to stop
      return;
    }
    if (contextKeyRef.current !== key) {
      contextKeyRef.current = key;
      // Abandon in-flight speech: a trailing final must not land in the new context.
      stopRef.current({ discard: true });
    }
  }, [matterGuid, chatGuid]);

  // Tear everything down on unmount so a navigated-away session never leaks the mic.
  // Marking unmounted also aborts any in-flight start() before it opens the mic.
  // Re-assert mounted on (re)mount: React 18 StrictMode mounts, cleans up (setting
  // this false), then remounts — without resetting here, mountedRef would stay false
  // and every start() would treat the live component as unmounted and bail.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      sessionGenRef.current += 1;
      teardownRecognizer();
    };
  }, [teardownRecognizer]);

  return {
    isSupported,
    isRecording,
    isConnecting,
    error,
    clearError,
    start,
    stop,
    toggle,
    prewarm,
  };
}
